import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuditEvent, ReviewStatus, VersionReview } from '@katsquare/types';
import { Button, Spinner } from '../../components/ui';
import { useToast } from '../../components/Toast';
import { getAudit, getReview, submitDecision, withdrawDecision } from '../../lib/api';

const STATUS: Record<ReviewStatus, { label: string; cls: string }> = {
  NO_GATES: { label: 'In review', cls: 'bg-amber-100 text-amber-700' },
  IN_REVIEW: { label: 'In review', cls: 'bg-amber-100 text-amber-700' },
  CHANGES_REQUESTED: { label: 'In review', cls: 'bg-amber-100 text-amber-700' },
  APPROVED: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700' },
};

/**
 * Inline review gate for the single page header. A proof is "In review" until
 * someone approves it, then "Approved" (with Reopen). The backend auto-provisions
 * a single implicit approval gate per proof, so there's no workflow to configure.
 */
export function ReviewControls({ versionId, proofId }: { versionId: string; proofId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showActivity, setShowActivity] = useState(false);

  const review = useQuery({
    queryKey: ['review', versionId],
    queryFn: () => getReview(versionId),
    // Keep the previous version's controls visible while the new one loads, so
    // switching versions doesn't flash / resize the header.
    placeholderData: keepPreviousData,
  });
  const audit = useQuery({
    queryKey: ['audit', proofId],
    queryFn: () => getAudit(proofId),
    enabled: showActivity,
  });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['review', versionId] });
    queryClient.invalidateQueries({ queryKey: ['audit', proofId] });
  };

  const approve = useMutation({
    mutationFn: (stepId: string) => submitDecision(versionId, stepId, { decision: 'APPROVED' }),
    onSuccess: invalidate,
    onError: (e: Error) => toast(e.message),
  });
  const reopen = useMutation({
    mutationFn: (stepId: string) => withdrawDecision(versionId, stepId),
    onSuccess: invalidate,
    onError: (e: Error) => toast(e.message),
  });

  if (review.isPending) return <Spinner />;
  const r = review.data as VersionReview;
  const gate = r.steps[0]; // the single approval gate
  const approved = r.status === 'APPROVED';
  const approver = gate?.approvals.find((a) => a.decision === 'APPROVED')?.user ?? null;

  return (
    <>
      <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-semibold ${STATUS[r.status].cls}`}>
        {STATUS[r.status].label}
      </span>
      {approved && approver && (
        <span className="hidden text-xs text-neutral-500 xl:inline">by {approver.name}</span>
      )}
      {gate && !approved && (
        <Button
          variant="success"
          onClick={() => approve.mutate(gate.step.id)}
          disabled={approve.isPending}
          className="px-3 py-1.5 text-sm"
        >
          {approve.isPending ? <Spinner /> : 'Approve'}
        </Button>
      )}
      {gate && approved && (
        <Button
          variant="ghost"
          onClick={() => reopen.mutate(gate.step.id)}
          disabled={reopen.isPending}
          className="border border-neutral-200 px-3 py-1.5 text-sm"
        >
          {reopen.isPending ? <Spinner /> : 'Reopen'}
        </Button>
      )}
      <div className="relative">
        <Button
          variant="ghost"
          onClick={() => setShowActivity((a) => !a)}
          className="border border-neutral-200 px-3 py-1.5 text-sm"
        >
          Activity
        </Button>
        {showActivity && (
          <div className="absolute right-0 top-full z-30 mt-1 w-[calc(100vw-1rem)] max-w-sm overflow-hidden rounded-md border border-neutral-200 bg-white shadow-lg sm:w-96 sm:max-w-none">
            <ActivityFeed events={audit.data} loading={audit.isPending} />
          </div>
        )}
      </div>
    </>
  );
}

function ActivityFeed({ events, loading }: { events: AuditEvent[] | undefined; loading: boolean }) {
  return (
    <div className="max-h-64 overflow-y-auto bg-neutral-50 px-4 py-2">
      {loading ? (
        <div className="flex justify-center py-3">
          <Spinner />
        </div>
      ) : !events || events.length === 0 ? (
        <p className="py-2 text-xs text-neutral-400">No activity yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {events.map((e) => (
            <li key={e.id} className="flex items-baseline gap-2 text-xs">
              <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${dotFor(e.type)}`} />
              <span className="text-neutral-700">{e.summary}</span>
              <span className="ml-auto shrink-0 text-neutral-400">
                {/* Guests have no user row; their summary already names them. */}
                {e.actor ? `${e.actor.name.split(' ')[0]} · ` : ''}
                {new Date(e.createdAt).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function dotFor(type: string): string {
  if (type === 'review.approved') return 'bg-emerald-500';
  if (type.startsWith('approval')) return 'bg-blue-500';
  if (type.startsWith('version')) return 'bg-amber-500';
  if (type.startsWith('comment')) return 'bg-violet-500';
  return 'bg-neutral-400';
}
